import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { api, formatApiError } from "../lib/api";
import { Building2, KeyRound, MailCheck } from "lucide-react";
import { toast } from "sonner";

export default function LoginPage() {
  const { login, refresh } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("password"); // password | otp
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  // OTP state
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");

  function routeAfterLogin(u) {
    nav(u.role === "super_admin" ? "/super-admin" : u.role === "staff" ? "/staff" : "/dashboard");
  }

  async function submitPassword(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome, ${u.name}`);
      routeAfterLogin(u);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  }

  async function requestOtp(e) {
    e.preventDefault();
    if (!email) return toast.error("Enter your email first");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/otp/request", { email });
      toast.success(data.message || "Code sent — check your inbox");
      setOtpSent(true);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  }

  async function verifyOtp(e) {
    e.preventDefault();
    if (otp.length !== 6) return toast.error("Enter the 6-digit code");
    setBusy(true);
    try {
      const { data: u } = await api.post("/auth/otp/verify", { email, code: otp });
      await refresh();
      toast.success(`Welcome, ${u.name}`);
      routeAfterLogin(u);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  }

  function fillDemo(e, p) { setEmail(e); setPassword(p); setMode("password"); }

  return (
    <div className="min-h-screen flex flex-col md:grid md:grid-cols-2 bg-[#FDFBF7]">
      <div className="hidden md:flex flex-col justify-between p-12 bg-[#F4F1EA] paper-grain relative overflow-hidden">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-[#C05640] text-white grid place-items-center">
            <Building2 size={20} />
          </div>
          <div>
            <div className="font-serif-display text-xl font-semibold leading-none">NivasOne</div>
            <div className="text-[11px] text-stone-500 tracking-wide">Apartment OS</div>
          </div>
        </div>
        <div className="max-w-md">
          <h1 className="font-serif-display text-5xl font-medium tracking-tight text-stone-900 leading-[1.05]">
            Your apartment, finally on one page.
          </h1>
          <p className="mt-5 text-stone-700 leading-relaxed">
            For small Indian apartment buildings — better than a WhatsApp group, lighter than a society ERP. Issues, meetings, notices, all searchable and easy to share.
          </p>
        </div>
        <div className="text-xs text-stone-500">A neighborhood notebook, not a corporate dashboard.</div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md bg-white rounded-2xl border border-stone-200 p-8 shadow-sm" data-testid="login-card">
          <div className="md:hidden flex items-center gap-2 mb-6">
            <div className="w-9 h-9 rounded-xl bg-[#C05640] text-white grid place-items-center">
              <Building2 size={20} />
            </div>
            <div>
              <div className="font-serif-display text-xl font-semibold leading-none">NivasOne</div>
              <div className="text-[11px] text-stone-500">Apartment OS</div>
            </div>
          </div>
          <h2 className="font-serif-display text-3xl font-semibold text-stone-900">Sign in</h2>
          <p className="text-sm text-stone-500 mt-1">Welcome back to your community.</p>

          <div className="mt-5 inline-flex border border-stone-200 rounded-full bg-stone-50 text-xs overflow-hidden" data-testid="login-mode-toggle">
            <button type="button" onClick={() => { setMode("password"); setOtpSent(false); setOtp(""); }}
                    data-testid="login-mode-password"
                    className={`px-3.5 py-1.5 font-semibold ${mode === "password" ? "bg-[#C05640] text-white" : "text-stone-600"}`}>
              <KeyRound size={12} className="inline mr-1" /> Password
            </button>
            <button type="button" onClick={() => setMode("otp")}
                    data-testid="login-mode-otp"
                    className={`px-3.5 py-1.5 font-semibold ${mode === "otp" ? "bg-[#C05640] text-white" : "text-stone-600"}`}>
              <MailCheck size={12} className="inline mr-1" /> Email code
            </button>
          </div>

          {mode === "password" ? (
            <form onSubmit={submitPassword} className="mt-5 space-y-4" data-testid="login-form">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" required value={email}
                       onChange={(e) => setEmail(e.target.value)}
                       data-testid="login-email"
                       placeholder="you@apartment.in" className="h-12 mt-1.5" />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Password</Label>
                  <ForgotPasswordLink initialEmail={email} />
                </div>
                <Input id="password" type="password" required value={password}
                       onChange={(e) => setPassword(e.target.value)}
                       data-testid="login-password"
                       placeholder="••••••••" className="h-12 mt-1.5" />
              </div>
              <Button type="submit" disabled={busy} data-testid="login-submit"
                      className="w-full h-12 rounded-full bg-[#C05640] hover:bg-[#A54734] text-white text-base font-semibold">
                {busy ? "Signing in…" : "Sign in"}
              </Button>
            </form>
          ) : (
            <div className="mt-5 space-y-4" data-testid="otp-form">
              <div>
                <Label htmlFor="otp-email">Email</Label>
                <Input id="otp-email" type="email" required value={email}
                       onChange={(e) => setEmail(e.target.value)}
                       data-testid="otp-email"
                       placeholder="you@apartment.in" className="h-12 mt-1.5" disabled={otpSent} />
              </div>
              {!otpSent ? (
                <Button onClick={requestOtp} disabled={busy || !email} data-testid="otp-request"
                        className="w-full h-12 rounded-full bg-[#C05640] hover:bg-[#A54734] text-white text-base font-semibold">
                  {busy ? "Sending…" : "Send me a sign-in code"}
                </Button>
              ) : (
                <form onSubmit={verifyOtp} className="space-y-4" data-testid="otp-verify-form">
                  <div>
                    <Label htmlFor="otp">6-digit code from email</Label>
                    <Input id="otp" required inputMode="numeric" pattern="[0-9]{6}" maxLength={6}
                           value={otp}
                           onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                           data-testid="otp-code"
                           placeholder="123456" className="h-12 mt-1.5 tracking-[0.5em] text-center font-mono text-lg" />
                    <p className="text-[11px] text-stone-500 mt-1.5">Code sent to <b>{email}</b>. Valid for 10 minutes.</p>
                  </div>
                  <Button type="submit" disabled={busy || otp.length !== 6} data-testid="otp-submit"
                          className="w-full h-12 rounded-full bg-[#C05640] hover:bg-[#A54734] text-white text-base font-semibold">
                    {busy ? "Signing in…" : "Sign in"}
                  </Button>
                  <button type="button" onClick={() => { setOtpSent(false); setOtp(""); }}
                          data-testid="otp-reset" className="text-xs text-stone-500 hover:text-[#C05640] w-full">
                    Use a different email
                  </button>
                </form>
              )}
            </div>
          )}

          <p className="text-sm text-stone-500 mt-5 text-center">
            New here? <Link to="/register" className="text-[#C05640] font-semibold" data-testid="goto-register">Set up your apartment</Link>
          </p>
          <p className="text-xs text-stone-400 mt-1 text-center">
            Not signed up yet? <Link to="/request-demo" className="text-[#C05640] underline-offset-2 hover:underline" data-testid="goto-demo">Request a demo</Link>
          </p>

          <div className="mt-6 pt-6 border-t border-stone-200">
            <div className="text-[11px] uppercase tracking-wider text-stone-500 font-bold mb-2">Demo accounts</div>
            <div className="space-y-1 text-xs text-stone-600">
              <button type="button" onClick={() => fillDemo("demoadmin@nivasone.in", "admin123")}
                      data-testid="demo-committee"
                      className="block hover:text-[#C05640] underline-offset-2 hover:underline">
                Committee (Anita) — demoadmin@nivasone.in
              </button>
              <button type="button" onClick={() => fillDemo("demoowner@nivasone.in", "resident123")}
                      data-testid="demo-resident"
                      className="block hover:text-[#C05640] underline-offset-2 hover:underline">
                Resident (Vikram) — demoowner@nivasone.in
              </button>
              <button type="button" onClick={() => fillDemo("demoguard@nivasone.in", "guard123")}
                      data-testid="demo-staff"
                      className="block hover:text-[#C05640] underline-offset-2 hover:underline">
                Staff (Ramesh) — demoguard@nivasone.in
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ForgotPasswordLink({ initialEmail }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState(initialEmail || "");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setSent(true);
      toast.success(data.message || "If we have your email, a reset link is on its way.");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => {
      setOpen(v);
      if (!v) { setSent(false); }
      if (v && initialEmail && !email) setEmail(initialEmail);
    }}>
      <DialogTrigger asChild>
        <button type="button" className="text-xs text-[#C05640] hover:underline" data-testid="forgot-password-link">
          Forgot password?
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-sm rounded-2xl" data-testid="forgot-password-dialog">
        <DialogHeader><DialogTitle className="font-serif-display">Reset your password</DialogTitle></DialogHeader>
        {sent ? (
          <div className="space-y-3" data-testid="forgot-sent">
            <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-800">
              If an account exists for <b>{email}</b>, we&apos;ve sent a reset link. Check your inbox (and spam). The link is valid for 30 minutes.
            </div>
            <Button onClick={() => setOpen(false)} data-testid="forgot-close"
                    className="w-full rounded-full bg-stone-800 hover:bg-stone-900">Got it</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3" data-testid="forgot-form">
            <p className="text-sm text-stone-600">We&apos;ll email you a secure link to set a new password.</p>
            <div>
              <Label>Email</Label>
              <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                     data-testid="forgot-email" placeholder="you@apartment.in" className="h-11 mt-1.5" />
            </div>
            <Button type="submit" disabled={busy} data-testid="forgot-submit"
                    className="w-full h-11 rounded-full bg-[#C05640] hover:bg-[#A54734]">
              {busy ? "Sending…" : "Send reset link"}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
